import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Bell, Check, CheckCheck, X, Loader2 } from 'lucide-react';
import { resolveNotificationLink } from '../utils/notificationLinks';

export function NotificationBell() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/api/notifications/?limit=20');
      setNotifications(data.notifications || []);
      setUnreadCount((data.notifications || []).filter(n => !n.is_read).length);
    } catch (e) {
      console.error('Failed to load notifications', e);
    }
  }, [api, user]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  // Poll every 30s
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, user]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) &&
          btnRef.current && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Lock body scroll on mobile when open
  useEffect(() => {
    if (open && window.innerWidth < 640) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const togglePanel = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 640;
      setPosition({
        top: isMobile ? 0 : rect.bottom + 8,
        left: isMobile ? 0 : Math.min(rect.right - 380, window.innerWidth - 390),
      });
    }
    setOpen(!open);
  };

  const markRead = async (id) => {
    try {
      await api.post(`/api/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch (e) { console.error('Mark read failed:', e); }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await api.post('/api/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error('Failed to mark all as read', e);
    }
    setLoading(false);
  };

  const handleNotifClick = async (n) => {
    if (!n.is_read) {
      await markRead(n.id);
    }
    const targetLink = resolveNotificationLink(n.title, user?.role, n.link);
    if (targetLink) {
      setOpen(false);
      navigate(targetLink);
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'success': return 'bg-[#00E676]';
      case 'warning': return 'bg-[#FFEA00]';
      case 'error': return 'bg-[#FF1744]';
      default: return 'bg-[#00E5FF]';
    }
  };

  const timeAgo = (dateStr) => {
    try {
      const d = new Date(dateStr);
      const now = new Date();
      const secs = Math.floor((now - d) / 1000);
      if (secs < 60) return 'just now';
      if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
      if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
      return `${Math.floor(secs / 86400)}d ago`;
    } catch { return ''; }
  };

  return (
    <>
      {/* Bell Button */}
      <button
        ref={btnRef}
        onClick={togglePanel}
        className="relative p-2 rounded-lg text-[#A0B4CC] hover:text-white hover:bg-white/10 transition-all"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-[#FF1744] text-white text-[9px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1 leading-none ring-2 ring-[#041A3B]"
            data-testid="notification-count"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel — rendered via Portal to escape sidebar z-index/overflow */}
      {open && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[299] sm:bg-transparent bg-black/50"
            onClick={() => setOpen(false)}
          />

          {/* Panel */}
          <div
            ref={panelRef}
            style={window.innerWidth >= 640 ? { position: 'fixed', top: position.top, left: Math.max(8, position.left), zIndex: 300 } : { zIndex: 300 }}
            className={`
              ${window.innerWidth < 640 ? 'fixed inset-x-0 bottom-0 top-auto' : ''}
              w-full sm:w-[380px]
              max-h-[80vh] sm:max-h-[70vh]
              bg-[#041A3B] border border-white/15
              ${window.innerWidth < 640 ? 'rounded-t-2xl' : 'rounded-xl'}
              shadow-2xl
              flex flex-col overflow-hidden
            `}
            data-testid="notification-panel"
          >
            {/* Mobile drag handle */}
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 rounded-full bg-white/20" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <h3 className="text-white font-outfit font-semibold text-sm">
                Notifications
                {unreadCount > 0 && (
                  <span className="ml-2 text-[10px] font-normal text-[#00E5FF] bg-[#00E5FF]/10 px-2 py-0.5 rounded-full">
                    {unreadCount} new
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    disabled={loading}
                    className="text-[#00E5FF] text-xs hover:underline flex items-center gap-1"
                    data-testid="mark-all-read-btn"
                  >
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCheck className="w-3 h-3" />}
                    <span className="hidden sm:inline">Mark all read</span>
                    <span className="sm:hidden">Read all</span>
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="text-[#5C7599] hover:text-white p-1 rounded-md hover:bg-white/10 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Notification List */}
            <div className="overflow-y-auto flex-1 divide-y divide-white/5 overscroll-contain">
              {notifications.length === 0 ? (
                <div className="py-16 sm:py-12 text-center">
                  <Bell className="w-10 h-10 sm:w-8 sm:h-8 text-[#5C7599]/30 mx-auto mb-3" />
                  <p className="text-[#5C7599] text-sm">No notifications yet</p>
                  <p className="text-[#5C7599]/50 text-xs mt-1">You&apos;ll see updates here</p>
                </div>
              ) : (
                notifications.map(n => (
                  <div
                    key={n.id}
                    className={`px-4 py-3 hover:bg-white/5 active:bg-white/10 cursor-pointer transition-colors ${!n.is_read ? 'bg-[#00E5FF]/5' : ''} ${n.link ? 'cursor-pointer' : ''}`}
                    onClick={() => handleNotifClick(n)}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="flex gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${getTypeColor(n.type)}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm leading-snug ${!n.is_read ? 'text-white font-medium' : 'text-[#A0B4CC]'}`}>
                          {n.title}
                        </p>
                        <p className="text-[#5C7599] text-xs mt-0.5 line-clamp-2">{n.message}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-[#5C7599]/50 text-[10px]">{timeAgo(n.created_at)}</p>
                          {n.link && <span className="text-[#00E5FF]/50 text-[10px]">Tap to view</span>}
                        </div>
                      </div>
                      {!n.is_read && (
                        <div className="flex-shrink-0 mt-1.5">
                          <div className="w-2 h-2 rounded-full bg-[#00E5FF] animate-pulse" />
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer — safe area for mobile */}
            <div className="px-4 py-2 sm:py-1.5 border-t border-white/5 pb-safe">
              <p className="text-[#5C7599]/50 text-[10px] text-center">
                Showing last {notifications.length} notifications
              </p>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
}
